/** The projected pick, debrief slide and delivered surprise (screens §F8). Facts only: gated event facts are omitted, never locked. */
import type { DebriefPickView, DebriefSlide, EventCardView } from "@bsim/contracts";
import { useRoute } from "../api/context";
import { HIT_SOURCES, factLabel, formatFactValue } from "../events/facts";
import { formatKpi, formatPp, formatUsdPerShare } from "../format";
import { printedParts } from "../score/labels";
import { PICK_LABELS } from "./labels";
import { sentenceCase } from "../format/labels";
import { pickHeadline, pickPrompt } from "./pickWords";

export function Pick({ pick }: { readonly pick: DebriefPickView }) {
  return (
    <section className="gx-room-main" data-testid="room.pick">
      <div className="gx-room-eyebrow">Round {pick.roundNo} · {PICK_LABELS[pick.kind]} · {pick.teamName}</div>
      <h1 className="gx-room-headline">{pickHeadline(pick)}</h1>
      <p className="gx-room-prompt">{pickPrompt(pick)}</p>
    </section>
  );
}

/**
 * The data behind a slide, drawn once for the room and for the facilitator's preview of it.
 * Only the market slide has a source the contract already serves (`publicMarket`: value created and reported CET1 by bank, in the order given, never a rank or a supervisory reading);
 * the other five slides show their title and question until a contract batch adds their rows (wall: `DebriefSlide` rows).
 */
export function SlideData({ cohortId, slide }: { readonly cohortId: string; readonly slide: DebriefSlide }) {
  const market = useRoute("publicMarket", slide.kind === "market" || slide.kind === "valueMoves" ? { cohortId } : null);
  if ((slide.kind !== "market" && slide.kind !== "valueMoves") || market.data === undefined) return null;
  const banks = market.data.banks.filter((b) => b.score !== null);
  if (banks.length === 0) return null;
  const unscored = market.data.banks.length - banks.length;
  const unscoredLine = unscored === 0 ? null : <p className="gx-room-prompt" data-testid="room.slide.unscored">{unscored === 1 ? "1 bank is not scored yet" : `${unscored} banks are not scored yet`}</p>;
  // The forecasts and events slides stay title and question: their rows are a contract wall (`DebriefSlide` carries fact ids, no values).
  if (slide.kind === "valueMoves") {
    // The same four figures the rank card prints (`score.components`), in the order the market gives; a bank scored before components existed shows its total only.
    return (
      <>
      <p className="gx-room-prompt" data-testid="room.slide.after">After round {market.data.throughRoundNo}, against each bank&apos;s twin, per share</p>
      <ul className="gx-room-cards gx-room-cards--dense" data-testid="room.slide.data" aria-label={`What moved value after round ${market.data.throughRoundNo}`}>
        {banks.map((b) => {
          const c = b.score?.components;
          const usd = (v: number) => formatUsdPerShare(v, { signed: true });
          const parts = c === undefined || b.score === null ? null : printedParts(c, b.score.valueCreatedUsdPerShare);
          return (
            <li key={b.teamId}>
              <b>{b.name}</b>
              <span className="gx-num">{c === undefined ? "" : `Share price ${usd(parts?.price ?? c.priceVsTwinUsdPerShare)} · Cash returned ${usd(parts?.cash ?? c.capitalReturnedVsTwinUsdPerShare)} · Next two years ${usd(parts?.lookThrough ?? c.lookThroughAdjustmentUsdPerShare)}`}</span>
              <span className="gx-num">{b.score === null ? "" : `Value created ${usd(b.score.valueCreatedUsdPerShare)}/share`}</span>
            </li>
          );
        })}
      </ul>
      {unscoredLine}
      </>
    );
  }
  return (
    <>
    <p className="gx-room-prompt" data-testid="room.slide.after">After round {market.data.throughRoundNo}</p>
    <ul className="gx-room-cards gx-room-cards--dense" data-testid="room.slide.data" aria-label={`The market after round ${market.data.throughRoundNo}`}>
      {banks.map((b) => (
        <li key={b.teamId}>
          <b>{b.name}</b>
          <span className="gx-num">{b.score === null ? "" : `${formatUsdPerShare(b.score.valueCreatedUsdPerShare, { signed: true })}/share vs its twin · ${formatPp(b.score.valueCreatedRate, { signed: true })} official`}</span>
          <span className="gx-num">{b.kpis["cet1Reported"] === undefined ? "" : `Reported CET1 ${formatKpi("cet1Reported", b.kpis["cet1Reported"])}`}</span>
        </li>
      ))}
    </ul>
    {unscoredLine}
    </>
  );
}

export function Slide({ slide, cohortId }: { readonly slide: DebriefSlide; readonly cohortId: string }) {
  return (
    <section className="gx-room-main" data-testid="room.slide">
      <div className="gx-room-eyebrow">Slide {slide.slideNo} of 6</div>
      <h1 className="gx-room-headline">{slide.title}</h1>
      <p className="gx-room-prompt">{slide.prompt}</p>
      <SlideData cohortId={cohortId} slide={slide} />
    </section>
  );
}

export function Surprise({ card }: { readonly card: EventCardView }) {
  // The earnings hit says what it includes (the engine serves its four sources with it), so the sources are not tiles of their own here.
  const hitStated = card.shockFacts.some((f) => f.key === "thisYearEarningsHitUsd" && f.status === "shown");
  const shown = card.shockFacts.filter((f) => f.status === "shown" && !(hitStated && (HIT_SOURCES as readonly string[]).includes(f.key)));
  return (
    <section className="gx-room-main gx-room-surprise" data-testid="room.surprise">
      <div className="gx-room-eyebrow">{card.category} · live now</div>
      <h1 className="gx-room-headline gx-room-headline--sm">{sentenceCase(card.title)}</h1>
      <div className="gx-room-surprise-cols">
        <div>
          {card.narrative.map((p) => <p key={p} className="gx-room-body">{p}</p>)}
          {shown.length === 0 ? null : (
            <dl className="gx-room-facts gx-num">
              {shown.map((f) => f.status === "shown" ? <div key={f.key}><dt>{factLabel(f.key)}</dt><dd>{formatFactValue(f.key, f.value)}</dd></div> : null)}
            </dl>
          )}
        </div>
        <ul className="gx-room-choices">
          {card.choices.map((c) => (
            <li key={c.id}>
              <b>{c.label}</b>
              {c.summary === c.label ? null : <span>{c.summary}</span>}
              <ul>{c.tradeOffs.map((t) => <li key={t}>{t}</li>)}</ul>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
